<template>
  <div class="mail-thread">
    <div class="mail-thread-top">
      <div class="mail-thread-subject">{{ threadSubject }}</div>
      <div class="mail-thread-count">{{ mails.length }} messaggi</div>
    </div>

    <div
      v-for="(mail, index) in mails"
      :key="mail.id || index"
      class="mail-thread-item"
      :class="{ 'is-outbound': isOutbound(mail) }"
    >
      <button type="button" class="mail-thread-item-head" @click="toggle(index)">
        <div class="mail-thread-avatar" :style="{ backgroundColor: colorFor(mail) }">
          {{ initialsFor(mail) }}
        </div>
        <div class="mail-thread-item-meta">
          <div class="mail-thread-from">
            <strong>{{ fromName(mail) }}</strong>
            <span class="mail-thread-email" v-if="mail.from_email">&lt;{{ mail.from_email }}&gt;</span>
          </div>
          <div class="mail-thread-to" v-if="toLabel(mail)">A: {{ toLabel(mail) }}</div>
        </div>
        <div class="mail-thread-date">{{ formatDate(mail.date) }}</div>
        <i class="pi" :class="expanded[index] ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
      </button>

      <div v-show="expanded[index]" class="mail-thread-item-body">
        <TicketMailView :mail="mail" :compact="true" />
        <div
          v-if="(mail.visible_attachments || []).length > 0"
          class="mail-thread-attachments"
        >
          <div
            v-for="attachment in mail.visible_attachments"
            :key="attachment.mail_attachment_id || attachment.name"
            class="mail-thread-attachment"
          >
            <span><i class="pi pi-file mr-2"></i>{{ attachment.name }}</span>
            <span>
              <i class="pi pi-eye mr-3 clickable" @click="openAttachment(attachment)"></i>
              <i class="pi pi-download clickable" @click="downloadAttachment(attachment)"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject, reactive, watch } from 'vue'
import moment from 'moment'
import TicketMailView from '@/components/tickets/TicketMailView.vue'

export default {
  name: 'TicketMailThread',
  components: { TicketMailView },
  props: {
    mails: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const ticketChat = inject('ticketChat')
    const expanded = reactive({})

    const initExpanded = () => {
      Object.keys(expanded).forEach((k) => delete expanded[k])
      const list = props.mails || []
      list.forEach((_, i) => {
        // Ultimo messaggio sempre aperto; gli altri chiusi se thread lungo
        expanded[i] = list.length <= 2 || i === list.length - 1 || i === 0
      })
    }

    watch(() => props.mails, initExpanded, { immediate: true, deep: false })

    const threadSubject = computed(() => {
      const last = [...(props.mails || [])].reverse().find((m) => m?.subject)
      const first = (props.mails || []).find((m) => m?.subject)
      return (last || first)?.subject || 'Conversazione email'
    })

    const toggle = (index) => {
      expanded[index] = !expanded[index]
    }

    const formatAddresses = (list) => {
      if (!Array.isArray(list) || !list.length) return ''
      return list
        .map((item) => {
          if (!item) return ''
          if (typeof item === 'string') return item
          const email = item.email || item.address || ''
          const name = item.name || ''
          if (name && email) return `${name} <${email}>`
          return name || email
        })
        .filter(Boolean)
        .join(', ')
    }

    const fromName = (mail) => mail?.from_name || mail?.from_email || 'Mittente'
    const toLabel = (mail) => formatAddresses(mail?.to_addresses)
    const formatDate = (value) => {
      const m = moment(value)
      return m.isValid() ? m.locale('it').format('DD MMM YYYY HH:mm') : (value || '')
    }

    const initialsFor = (mail) => {
      const label = fromName(mail)
      const parts = String(label).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }

    const colorFor = (mail) => {
      const fn = ticketChat?.randomColorFromString
      return typeof fn === 'function' ? fn(fromName(mail)) : '#64748b'
    }

    const isOutbound = (mail) => Boolean(mail?.locally_created)

    return {
      expanded,
      threadSubject,
      toggle,
      fromName,
      toLabel,
      formatDate,
      initialsFor,
      colorFor,
      isOutbound,
      openAttachment: ticketChat?.openAttachment,
      downloadAttachment: ticketChat?.downloadAttachment,
    }
  },
}
</script>

<style scoped>
.mail-thread {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  margin: 8px 12px 16px;
}

.mail-thread-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.mail-thread-subject {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.mail-thread-count {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
}

.mail-thread-item + .mail-thread-item {
  border-top: 1px solid #eef2f7;
}

.mail-thread-item-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.mail-thread-item-head:hover {
  background: #f9fafb;
}

.mail-thread-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.mail-thread-item-meta {
  flex: 1;
  min-width: 0;
}

.mail-thread-from {
  font-size: 13px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mail-thread-email {
  color: #6b7280;
  font-weight: 400;
  margin-left: 4px;
}

.mail-thread-to {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mail-thread-date {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.mail-thread-item-body {
  padding: 0 12px 14px;
}

.mail-thread-attachments {
  margin-top: 10px;
  border-top: 1px dashed #e5e7eb;
  padding-top: 8px;
}

.mail-thread-attachment {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 4px 0;
  color: #374151;
}

.clickable {
  cursor: pointer;
  color: var(--primary-color, #3b82f6);
}

.mail-thread-item.is-outbound .mail-thread-item-head {
  background: #f8fbff;
}
</style>
